import { Check, ChevronDown, History, Pin, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { resolveApiAssetUrl, type CompanyContext } from "@/shared/api/api-client";
import { filterBySearch } from "@/shared/utils/text-search";

type Props = {
  /** A empresa que o painel está mostrando agora. */
  company: CompanyContext;
  /** O período dos números, curto: "7 dias · até 10/10/2026". */
  windowLabel: string;
  /** As empresas que dá pra fixar (as ativas que a pessoa acessa). */
  companies: CompanyContext[];
  /** A empresa fixada, ou nulo no modo automático (última aberta). */
  fixedId: string | null;
  onChoose: (companyId: string | null) => void;
};

/**
 * O selo da empresa no topo da Home, que também escolhe DE QUEM é o painel
 * (2026-10-16): no modo automático é a última empresa aberta (o de sempre);
 * fixando uma, hero, cartões e faixa de Imprensa ficam nela até a pessoa
 * voltar ao automático. A escolha mora no navegador, como as recentes.
 */
export function HeroCompanyPicker({ company, windowLabel, companies, fixedId, onChoose }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (companyId: string | null) => {
    onChoose(companyId);
    setOpen(false);
    setQuery("");
  };

  const shown = filterBySearch(
    [...companies].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    query,
    item => [item.name]
  );

  return (
    <div className={`home-hero-picker${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="home-hero-company"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(value => !value)}
      >
        <span className={`home-hero-mark${company.logo_url ? " has-logo" : ""}`}>
          {company.logo_url ? (
            <img src={resolveApiAssetUrl(company.logo_url)} alt="" />
          ) : (
            company.name.slice(0, 2).toUpperCase()
          )}
        </span>
        <span className="home-hero-company-text">
          <strong>{company.name}</strong>
          <span>
            {fixedId ? (
              <>
                <Pin size={11} aria-hidden="true" className="home-hero-fixed-icon" />
                Fixada · {windowLabel}
              </>
            ) : (
              windowLabel
            )}
          </span>
        </span>
        <ChevronDown size={16} aria-hidden="true" className="home-hero-chevron" />
      </button>

      {open && (
        <div className="home-hero-menu" id={listId} role="dialog" aria-label="Empresa do painel">
          <p className="home-hero-menu-label">Mostrar no painel</p>
          <button
            type="button"
            className={`home-hero-menu-item${fixedId === null ? " is-selected" : ""}`}
            onClick={() => choose(null)}
          >
            <span className="home-hero-menu-icon"><History size={15} aria-hidden="true" /></span>
            <span className="home-hero-menu-text">
              <strong>Última empresa aberta</strong>
              <small>Muda sozinho quando você abre outro cliente</small>
            </span>
            {fixedId === null && <Check size={15} aria-hidden="true" className="home-hero-menu-check" />}
          </button>

          <p className="home-hero-menu-label">Fixar uma empresa</p>
          <label className="home-hero-menu-search">
            <Search size={14} aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar empresa…"
              aria-label="Buscar empresa"
            />
          </label>
          <div className="home-hero-menu-list">
            {shown.map(item => (
              <button
                key={item.id}
                type="button"
                className={`home-hero-menu-item${fixedId === item.id ? " is-selected" : ""}`}
                onClick={() => choose(item.id)}
              >
                <span className={`home-hero-menu-mark${item.logo_url ? " has-logo" : ""}`}>
                  {item.logo_url ? <img src={resolveApiAssetUrl(item.logo_url)} alt="" /> : item.name.slice(0, 2).toUpperCase()}
                </span>
                <span className="home-hero-menu-text"><strong>{item.name}</strong></span>
                {fixedId === item.id && <Check size={15} aria-hidden="true" className="home-hero-menu-check" />}
              </button>
            ))}
            {shown.length === 0 && <p className="home-hero-menu-empty">Nenhuma empresa encontrada</p>}
          </div>
        </div>
      )}
    </div>
  );
}
